import { JIRA_AI_NATIVE_SIGNUP_SOURCE } from '@/components/user-context/helpers/cohorts';
import { experiments } from '@/utils/experiment/manifest';

import {
	queryParam,
	user,
	localStorageData,
	featureGates,
	type Metadata,
} from '../../hooks/use-registered-metadata';

import {
	JTBD,
	PROVISIONING,
	ScreenConfig,
	TEAM_CHOICE,
	TEMPLATE_CHOICE,
	PROVISIONING_WITH_WIZARD,
	RESOLVE_TENANT_LOADER,
	CODING_AGENT_CHOICE,
	WORK_STYLE_CHOICE,
	type Condition,
	type ConditionPair,
} from './utils';

const workStyleChoiceExperimentCondition = (value: boolean): ConditionPair<Metadata> => ({
	type: 'experiment',
	experiment: experiments.jiraSignupWorkStyleChoice,
	value,
});

const unlessReplacedByWorkStyleChoice = (
	conditions: ConditionPair<Metadata>[],
): Condition<Metadata>[] => [
	{
		conditionalAND: [...conditions, workStyleChoiceExperimentCondition(false)],
	},
];

const jtbdProductsCondition: ConditionPair<Metadata> = {
	conditionalOR: [
		{
			conditionalAND: [
				{ key: queryParam.products, value: ['jira-software.ondemand'] },
				workStyleChoiceExperimentCondition(false),
			],
		},
		{
			conditionalAND: [{ key: queryParam.products, value: ['jira-core.ondemand'] }],
		},
	],
};

export const jiraScreenConfig: ScreenConfig = {
	[RESOLVE_TENANT_LOADER]: {
		position: { default: 50 },
		onMount: () => undefined,
		visibility: {
			default: true,
		},
	},
	[TEAM_CHOICE]: {
		position: { default: 100 },
		onMount: () => undefined,
		visibility: {
			default: false,
			conditionalOR: [
				{
					conditionalAND: [
						{
							key: queryParam.templateId,
							value: undefined,
						},
						{
							key: queryParam.products,
							value: ['jira-software.ondemand'],
						},
						{
							conditionalOR: [
								{
									conditionalAND: [
										{
											key: user.isN2n,
											value: true,
										},
									],
								},
								{
									conditionalAND: [
										{
											key: user.isExistingJiraUser,
											value: false,
										},
									],
								},
							],
						},
						{
							conditionalOR: [
								{
									conditionalAND: [
										{
											key: localStorageData.team,
											value: undefined,
										},
									],
								},
								{
									conditionalAND: [
										{
											key: localStorageData.team,
											value: '',
										},
									],
								},
							],
						},
					],
				},
			],
		},
	},
	[WORK_STYLE_CHOICE]: {
		position: { default: 150 },
		onMount: () => undefined,
		visibility: {
			default: false,
			conditionalOR: [
				{
					conditionalAND: [
						{
							key: queryParam.templateId,
							value: undefined,
						},
						{
							key: queryParam.templateChoice,
							value: undefined,
						},
						{
							key: queryParam.jtbd,
							value: undefined,
						},
						{
							key: queryParam.products,
							value: ['jira-software.ondemand'],
						},
						workStyleChoiceExperimentCondition(true),
					],
				},
			],
		},
	},
	[JTBD]: {
		position: { default: 190 },
		onMount: () => undefined,
		visibility: {
			default: false,
			conditionalOR: [
				{
					conditionalAND: [
						{ key: featureGates.jiraAiNativeJtbdRoutingFix, value: true },
						{ key: queryParam.signupSource, value: JIRA_AI_NATIVE_SIGNUP_SOURCE },
						{ key: queryParam.jtbd, value: undefined },
						{ key: queryParam.templateId, value: undefined },
						jtbdProductsCondition,
					],
				},
				{
					conditionalAND: [
						{
							key: user.isN2n,
							value: true,
						},
						{
							key: queryParam.jtbd,
							value: undefined,
						},
						{
							key: queryParam.templateId,
							value: undefined,
						},
						jtbdProductsCondition,
					],
				},
			],
		},
	},
	[TEMPLATE_CHOICE]: {
		position: { default: 200 },
		onMount: () => undefined,
		visibility: {
			default: false,
			conditionalOR: unlessReplacedByWorkStyleChoice([
				{
					key: queryParam.templateId,
					value: undefined,
				},
				{
					key: queryParam.templateChoice,
					value: undefined,
				},
				{
					key: queryParam.jtbd,
					value: undefined,
				},
				{
					key: queryParam.products,
					value: ['jira-software.ondemand'],
				},
			]),
		},
	},
	[PROVISIONING]: {
		position: { default: 275 },
		onMount: () => undefined,
		visibility: {
			default: false,
			conditionalOR: [
				{
					conditionalAND: [
						{
							key: user.isMobile,
							value: true,
						},
						{
							key: queryParam.signupSource,
							value: undefined,
						},
					],
				},
			],
		},
	},
	[CODING_AGENT_CHOICE]: {
		position: { default: 285 },
		onMount: () => undefined,
		visibility: {
			default: false,
			conditionalOR: [
				{
					conditionalAND: [
						{
							key: queryParam.codingAgents,
							value: undefined,
						},
						{
							key: queryParam.signupSource,
							value: JIRA_AI_NATIVE_SIGNUP_SOURCE,
						},
					],
				},
			],
		},
	},
	[PROVISIONING_WITH_WIZARD]: {
		position: { default: 300 },
		onMount: () => undefined,
		visibility: {
			default: false,
			conditionalOR: [
				{
					conditionalAND: [
						{
							key: user.isMobile,
							value: false,
						},
					],
				},
				{
					conditionalAND: [
						{
							key: user.isMobile,
							value: true,
						},
						{
							key: queryParam.signupSource,
							value: JIRA_AI_NATIVE_SIGNUP_SOURCE,
						},
					],
				},
			],
		},
	},
};
